import type { SignInStreamEvent } from '@acs/shared'
import { API_BASE } from './api'

/**
 * Opens the sign-in NDJSON stream for a customer and yields each event as it
 * arrives.
 *
 * NDJSON over a POST, not SSE: `EventSource` is GET-only, and this action is a
 * POST that starts real work. The body is read incrementally and split on
 * newlines, so the UI updates the moment a step lands rather than at the end.
 */
export async function* streamSignIn(
  customerId: string,
  signal?: AbortSignal
): AsyncGenerator<SignInStreamEvent> {
  const res = await fetch(`${API_BASE}/api/customers/${encodeURIComponent(customerId)}/signin`, {
    method: 'POST',
    headers: { Accept: 'application/x-ndjson' },
    credentials: 'include',
    signal,
  })

  if (!res.ok || !res.body) {
    let message = `Sign-in could not start (${res.status})`
    try {
      const body = (await res.json()) as { error?: { message?: string } }
      if (body.error?.message) message = body.error.message
    } catch {
      /* non-JSON error body */
    }
    yield { type: 'error', message }
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // A chunk can hold several lines, or a partial one; keep the remainder.
      let nl = buffer.indexOf('\n')
      while (nl !== -1) {
        const line = buffer.slice(0, nl).trim()
        buffer = buffer.slice(nl + 1)
        if (line) {
          const parsed = parse(line)
          if (parsed) yield parsed
        }
        nl = buffer.indexOf('\n')
      }
    }
    const tail = buffer.trim()
    if (tail) {
      const parsed = parse(tail)
      if (parsed) yield parsed
    }
  } finally {
    reader.releaseLock()
  }
}

function parse(line: string): SignInStreamEvent | null {
  try {
    return JSON.parse(line) as SignInStreamEvent
  } catch {
    return null
  }
}
